import { memo } from "react";

import Stack from "@mui/material/Stack";
import styled from "@mui/material/styles/styled";

import { Block, Marker } from "@app/analytics/components/PlayerControls/types";
import { getIntersectingAnnotationsFromTimeFrame } from "@app/analytics/components/ScrubberBar/utils";
import VideoAnnotation from "@app/analytics/components/VideoAnnotation";
import useMeetingReportHighlights from "@app/analytics/MeetingReport/hooks/useMeetingReportHighlights";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";

const Wrapper = styled(Stack)({
  backgroundColor: "rgba(0,0,0,0.4)",
  borderRadius: "4px",
  boxShadow:
    "0px 4px 8px rgba(0, 0, 0, 0.24), 0px 1px 1px rgba(0, 0, 0, 0.04);",
  padding: "4px 8px",
  position: "absolute",
  zIndex: 10,
  left: 8,
  top: 8,
  marginRight: "8px",
  maxWidth: "95%",
});

interface VideoAnnotationWrapperProps {
  markers?: Marker[];
  blocks?: Block[];
}

const VideoAnnotationWrapper = ({
  markers = [],
  blocks = [],
}: VideoAnnotationWrapperProps) => {
  const { currentTime, highlightAnnotationsEnabled, trailerActive } =
    useVideoStore((state) => ({
      currentTime: state.currentTimestamp,
      highlightAnnotationsEnabled: state.highlightAnnotationsEnabled,
      trailerActive: state.trailer.active,
    }));

  if (!highlightAnnotationsEnabled || trailerActive) return null;

  const timeFrame = {
    start: currentTime,
    end: currentTime,
  };

  const annotations = getIntersectingAnnotationsFromTimeFrame(
    timeFrame,
    blocks,
    markers,
    []
  );

  if (annotations.markers.length === 0 && annotations.blocks.length === 0) {
    return null;
  }

  return (
    <Wrapper className="video-annotations">
      {annotations.markers.map((marker) => (
        <VideoAnnotation
          key={marker.id}
          bgcolor={marker.bgcolor}
          color={marker.color}
          label={marker.label}
          description={marker.description}
          variant="circle"
        />
      ))}
      {annotations.blocks.map((block) => (
        <VideoAnnotation
          key={block.id}
          color={block.color}
          label={block.label}
          description={block.description}
          variant="square"
        />
      ))}
    </Wrapper>
  );
};

const VideoAnnotationContainer = memo(() => {
  const { blocks, markers } = useMeetingReportHighlights();
  return <VideoAnnotationWrapper blocks={blocks} markers={markers} />;
});

VideoAnnotationContainer.displayName = "VideoAnnotationContainer";

export default VideoAnnotationContainer;
